import { useAppSelector } from 'app/hooks';
import { queryClient } from 'lib/react-query';
import { useRef, useEffect, useState } from 'react';
import {
  calcTraceMax,
  calcTraceMin,
  parseTraceFromBuffer,
} from 'utils/traceUtils';

interface Props {
  snapshotId: number;
  initialMax: string | undefined;
  initialMin: string | undefined;
  minMaxEnabled: number | undefined;
  traceData: number[];
}

export const useMinMax = ({
  snapshotId,
  initialMax,
  initialMin,
  minMaxEnabled,
  traceData,
}: Props) => {
  const message = useAppSelector(
    (state) => state.spectrasWebSocketServer.message[snapshotId]
  );

  const maxTrace = useRef<number[]>([]);
  const minTrace = useRef<number[]>([]);
  const [reset, setReset] = useState<boolean>(false);

  useEffect(() => {
    if (!traceData || traceData.length === 0) {
      minTrace.current = [];
      maxTrace.current = [];
      return;
    }

    if (minMaxEnabled === 1 || minMaxEnabled === 3) {
      if (!reset && initialMin && minTrace.current.length === 0) {
        minTrace.current = parseTraceFromBuffer(initialMin);
      } else {
        minTrace.current = calcTraceMin(minTrace.current, traceData);
      }
    } else if (minTrace.current.length > 0) {
      minTrace.current = [];
    }

    if (minMaxEnabled === 2 || minMaxEnabled === 3) {
      if (!reset && initialMax && maxTrace.current.length === 0) {
        maxTrace.current = parseTraceFromBuffer(initialMax);
      } else {
        maxTrace.current = calcTraceMax(maxTrace.current, traceData);
      }
    } else if (maxTrace.current.length > 0) {
      maxTrace.current = [];
    }

    setReset(false);
  }, [traceData, minMaxEnabled, initialMin, initialMax, reset]);

  useEffect(() => {
    if (message?.type !== 'minmax_reset') return;
    minTrace.current = [];
    maxTrace.current = [];
    setReset(true);
    queryClient.invalidateQueries(['snapshot', snapshotId]);
  }, [message, snapshotId]);

  return [maxTrace.current, minTrace.current];
};
